/**
 * NavigationContext - Global navigation with parameters
 *
 * Provides cross-component navigation capabilities with parameter passing.
 * Used for features like clicking on issue pills to navigate to relevant workflows.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const NavigationContext = createContext(null);

export function NavigationProvider({ children, onNavigate }) {
  // Navigation parameters (e.g., which claim/maintenance to pre-select)
  const [viewParams, setViewParams] = useState({});

  /**
   * Navigate to a view with optional parameters
   * @param {string} view - The view ID to navigate to (e.g., 'claims', 'maintenance')
   * @param {object} params - Optional parameters (e.g., { selectClaimId: 123 })
   */
  const navigateTo = useCallback((view, params = {}) => {
    // Set parameters first (in context state)
    setViewParams(params);
    // Then trigger navigation - pass params to App's handleNavigate
    if (onNavigate) {
      onNavigate(view, params);
    }
  }, [onNavigate]);

  /**
   * Get current view parameters
   * @returns {object} Current parameters
   */
  const getParams = useCallback(() => {
    return viewParams;
  }, [viewParams]);

  /**
   * Clear view parameters (call after consuming them)
   */
  const clearParams = useCallback(() => {
    setViewParams({});
  }, []);

  /**
   * Navigate to a specific entity detail
   * Helper method for common navigation patterns
   */
  const navigateToEntity = useCallback((entityType, entityId, additionalParams = {}) => {
    const viewMap = {
      claim: 'claims',
      maintenance: 'maintenance',
      vehicle: 'vehicles',
      driver: 'drivers',
      insurance: 'insurance',
      inspection: 'inspections'
    };

    const view = viewMap[entityType];
    if (view) {
      navigateTo(view, {
        [`select${entityType.charAt(0).toUpperCase() + entityType.slice(1)}Id`]: entityId,
        ...additionalParams
      });
    }
  }, [navigateTo]);

  const value = React.useMemo(() => ({
    navigateTo,
    navigateToEntity,
    getParams,
    clearParams,
    viewParams
  }), [navigateTo, navigateToEntity, getParams, clearParams, viewParams]);

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

/**
 * Hook to access navigation context
 * @returns {object} Navigation context methods
 */
export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    // Return a no-op implementation if used outside provider
    // This allows components to be used standalone without errors
    return {
      navigateTo: () => console.warn('NavigationContext not available'),
      navigateToEntity: () => console.warn('NavigationContext not available'),
      getParams: () => ({}),
      clearParams: () => {},
      viewParams: {}
    };
  }
  return context;
}

export default NavigationContext;
